/**
 * Jubly SalesDrive — CRM Dashboard
 * Stats overview, quick actions, and recent activity feed
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Briefcase, Trophy, XCircle, CheckSquare, Check,
  FileText, Mail, MessageSquare, Phone, ArrowRight, Plus,
  TrendingUp, AlertCircle, Clock, User
} from 'lucide-react';

// Format cents to currency string
function formatCurrency(cents) {
  if (!cents && cents !== 0) return '$0';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(dollars);
}

// Relative time formatter
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Activity type → icon + color
function ActivityIcon({ type }) {
  const map = {
    contact_created:    { Icon: User,          bg: 'bg-blue-100',   color: 'text-blue-600' },
    deal_created:       { Icon: Briefcase,     bg: 'bg-indigo-100', color: 'text-indigo-600' },
    deal_stage_change:  { Icon: ArrowRight,    bg: 'bg-purple-100', color: 'text-purple-600' },
    deal_won:           { Icon: Trophy,        bg: 'bg-green-100',  color: 'text-green-600' },
    deal_lost:          { Icon: XCircle,       bg: 'bg-red-100',    color: 'text-red-500' },
    task_created:       { Icon: CheckSquare,   bg: 'bg-amber-100',  color: 'text-amber-600' },
    task_completed:     { Icon: Check,         bg: 'bg-green-100',  color: 'text-green-600' },
    note:               { Icon: FileText,      bg: 'bg-gray-100',   color: 'text-gray-600' },
    email_sent:         { Icon: Mail,          bg: 'bg-sky-100',    color: 'text-sky-600' },
    email_received:     { Icon: Mail,          bg: 'bg-sky-100',    color: 'text-sky-500' },
    sms_sent:           { Icon: MessageSquare, bg: 'bg-teal-100',   color: 'text-teal-600' },
    sms_received:       { Icon: MessageSquare, bg: 'bg-teal-100',   color: 'text-teal-500' },
    call_made:          { Icon: Phone,         bg: 'bg-violet-100', color: 'text-violet-600' },
    call_received:      { Icon: Phone,         bg: 'bg-violet-100', color: 'text-violet-500' },
    meeting:            { Icon: Users,         bg: 'bg-orange-100', color: 'text-orange-600' },
  };
  const { Icon, bg, color } = map[type] || { Icon: FileText, bg: 'bg-gray-100', color: 'text-gray-500' };
  return (
    <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
  );
}

// Stat card component
function StatCard({ label, value, sub, subColor = 'text-gray-500', icon: Icon, iconBg, iconColor, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-gray-300 transition-all group w-full"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
          {sub && <p className={`text-xs mt-1.5 font-medium ${subColor}`}>{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 ml-3`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </button>
  );
}

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/crm/dashboard', { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setStats(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm font-medium text-gray-700">Failed to load dashboard</p>
        <p className="text-xs text-gray-400 mt-1">{error}</p>
      </div>
    );
  }

  const {
    totalContacts = 0,
    totalDeals = 0,
    openDealValue = 0,
    wonDealValue = 0,
    tasksOverdue = 0,
    tasksDueToday = 0,
    recentActivities = [],
  } = stats || {};

  const tasksDueLabel = tasksDueToday > 0
    ? `${tasksDueToday} due today`
    : 'None due today';

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-gray-50/40">
      <div className="max-w-5xl w-full mx-auto px-6 py-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Sales Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Your CRM overview at a glance</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/sales/contacts?new=1')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Contacts"
            value={totalContacts.toLocaleString()}
            sub="All time"
            icon={Users}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
            onClick={() => navigate('/sales/contacts')}
          />
          <StatCard
            label="Open Deals"
            value={totalDeals.toLocaleString()}
            sub={formatCurrency(openDealValue) + ' pipeline'}
            icon={Briefcase}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
            onClick={() => navigate('/sales/pipeline')}
          />
          <StatCard
            label="Won Revenue"
            value={formatCurrency(wonDealValue)}
            sub="Closed won"
            subColor="text-green-600"
            icon={TrendingUp}
            iconBg="bg-green-50"
            iconColor="text-green-600"
            onClick={() => navigate('/sales/pipeline?status=won')}
          />
          <StatCard
            label="Tasks Due"
            value={tasksDueToday.toLocaleString()}
            sub={
              tasksOverdue > 0
                ? `${tasksOverdue} overdue`
                : tasksDueLabel
            }
            subColor={tasksOverdue > 0 ? 'text-red-500' : 'text-gray-500'}
            icon={Clock}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            onClick={() => navigate('/sales/tasks')}
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/sales/contacts?new=1')}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Users className="w-4 h-4" />
              + Contact
            </button>
            <button
              onClick={() => navigate('/sales/pipeline?new=1')}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <Briefcase className="w-4 h-4" />
              + Deal
            </button>
            <button
              onClick={() => navigate('/sales/tasks?new=1')}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 text-sm font-medium rounded-lg hover:bg-amber-100 transition-colors"
            >
              <CheckSquare className="w-4 h-4" />
              + Task
            </button>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Recent Activity</h2>
            <span className="text-xs text-gray-400">{recentActivities.length} events</span>
          </div>

          {recentActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FileText className="w-8 h-8 mb-2 stroke-1" />
              <p className="text-sm">No activity yet</p>
              <p className="text-xs mt-1">Actions you take will appear here</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {recentActivities.map((activity) => (
                <li key={activity.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50/60 transition-colors">
                  <ActivityIcon type={activity.activity_type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-snug">
                      {activity.title || activity.activity_type?.replace(/_/g, ' ')}
                    </p>
                    {activity.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {activity.description}
                      </p>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400 flex-shrink-0 mt-0.5">
                    {timeAgo(activity.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

      </div>
    </div>
  );
}
